import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Fuse from "fuse.js";
import {
  ResultsWrapper,
  ResultsList,
  ResultItem,
  ResultMain,
  EmptyResults,
  ResultTag,
} from "./styles";
import { getSafe } from "../../utils";
import EmptyIcon from "../../icons/EmptyIcon";
import { useFetchPlaces } from "../../hooks/useFetchPlaces";

// Props: searchTerm (string), onSelect(place), locale, selectedCities(array slugs), selectedTags(array slugs)
const SearchResult = ({
  searchTerm = "",
  onSelect,
  selectedCities = [],
  selectedTags = [],
}) => {
  const { data: placesResult } = useFetchPlaces();
  const places = useMemo(
    () => placesResult?.places ?? placesResult?.all ?? [],
    [placesResult]
  );
  const { local } = useSelector((state) => state.Local);
  const { t } = useTranslation();

  const fuse = useMemo(() => {
    return new Fuse(places || [], {
      keys: [{ name: `title.${local}`, weight: 0.6 }],

      threshold: 0.4,
      ignoreLocation: true,
      minMatchCharLength: 2,
    });
  }, [places, local]);

  const results = useMemo(() => {
    if (!searchTerm || searchTerm.trim().length === 0) return [];
    let out = fuse.search(searchTerm.trim()).map((r) => r.item);
    if (selectedCities.length > 0) {
      out = out.filter((p) => {
        return selectedCities.includes(p.contact?.address?.city);
      });
    }
    if (selectedTags.length > 0) {
      out = out.filter((p) => {
        const tagSlugs = (p.tags || []).map((t) => t.slug);
        return tagSlugs.some((t) => selectedTags.includes(t));
      });
    }
    return out;
  }, [fuse, searchTerm, selectedCities, selectedTags]);

  return (
    <ResultsWrapper>
      {results.length === 0 && (
        <EmptyResults>
          <span>
            <EmptyIcon />
          </span>
          <h1>oops</h1>
          <h2>{t("noResults")}</h2>
        </EmptyResults>
      )}
      {results.length > 0 && (
        <ResultsList>
          {results.map((item) => {
            return (
              <ResultItem key={item._id} onClick={() => onSelect?.(item)}>
                <ResultMain>{getSafe(() => item.title[local])}</ResultMain>
                <ResultTag>{getSafe(() => item.tags[0].name[local])}</ResultTag>
              </ResultItem>
            );
          })}
        </ResultsList>
      )}
    </ResultsWrapper>
  );
};

export default SearchResult;
